import type { Mail, BlobRef } from "../nostr/seal";
import { el } from "./render";

type Body = Pick<Mail, "text" | "html" | "text_ref" | "html_ref">;

/** Extract words without rendering sender markup or loading images. */
export function excerpt(body: Body): string {
  let text = body.text?.trim() ?? "";
  if (!text && body.html) {
    const doc = new DOMParser().parseFromString(body.html, "text/html");
    doc.querySelectorAll("script, style, head, iframe, object, embed, noscript, svg, form, template, [hidden]")
      .forEach((node) => node.remove());
    doc.querySelectorAll("p, div, br, hr, h1, h2, h3, h4, h5, h6, li, tr, blockquote, pre")
      .forEach((node) => node.append(doc.createTextNode("\n")));
    text = doc.body.textContent ?? "";
  }
  const lines = text.split(/\r?\n/).map((line) => line.replace(/\s+/g, " ").trim()).filter(Boolean);
  const start = lines.slice(0, 3).join("\n");
  const short = start.slice(0, 240).trimEnd();
  return short + (start.length > 240 || lines.length > 3 ? "…" : "");
}

export function screenerPreview(
  body: Body,
  load: (ref: BlobRef) => Promise<Uint8Array>,
): HTMLElement {
  const words = excerpt(body);
  const slot = el("div", { class: "preview" }, words || "No text preview.");
  if (words) return slot;

  const ref = body.text_ref ?? body.html_ref;
  if (!ref) return slot;
  slot.textContent = "Loading preview…";
  void load(ref).then((bytes) => {
    const decoded = new TextDecoder().decode(bytes);
    slot.textContent = excerpt(ref === body.text_ref ? { text: decoded } : { html: decoded }) || "No text preview.";
  }).catch(() => {
    slot.textContent = "Could not load the preview. Open the email to try again.";
  });
  return slot;
}
